import React from 'react';
import {
  Input,
  DatePicker,
  TimePicker,
  Switch,
  Select,
  Button,
  Popconfirm,
  Collapse,
  List,
  Spin,
  message,
} from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import type { CruisePortStatus, Trip, TripCityVisitMeta } from '../../types/client';
import { tripEditEn } from '../../content/trips.en';
import { isAtSeaLocation } from '../../services/cruiseItinerariesApi';
import {
  isGooglePlacesConfigured,
  searchPlaces,
  type PlaceSearchResult,
} from '../../services/googlePlacesService';
import {
  renameCity,
  setEmbarkDebark,
  updateCityMeta,
  removeCityFromDraft,
  applyPlaceToCity,
  setCityCoords,
} from '../../utils/tripEdit';
import { formatCardWeekdayDate } from '../../utils/tripDayCards';
import { ymdFromUnknown } from '../../utils/wallClock';
import styles from './EditTripCityPanel.module.css';

interface EditTripCityPanelProps {
  trip: Trip;
  cityId: string;
  onChange: (next: Trip) => void;
}

function ymd(d: Dayjs | null): string | null {
  return d ? d.format('YYYY-MM-DD') : null;
}

function hm(d: Dayjs | null): string | null {
  return d ? d.format('HH:mm') : null;
}

function toDay(iso: string | null | undefined): Dayjs | null {
  const ymd = ymdFromUnknown(iso);
  if (!ymd) return null;
  const d = dayjs(`${ymd}T12:00:00`);
  return d.isValid() ? d : null;
}

function toTime(hmValue: string | null | undefined): Dayjs | null {
  if (!hmValue) return null;
  const d = dayjs(hmValue, 'HH:mm');
  return d.isValid() ? d : null;
}

const EditTripCityPanel: React.FC<EditTripCityPanelProps> = ({ trip, cityId, onChange }) => {
  const name = trip.cityNames?.[cityId] ?? '';
  const meta: TripCityVisitMeta = trip.cityVisitMeta?.[cityId] ?? {
    arrivalDate: null,
    departureDate: null,
    portArrivalHm: null,
    portDepartureHm: null,
    cruisePortStatus: null,
  };
  const coords = trip.cityCoords?.[cityId] ?? null;
  const isCruise = trip.tripType === 'cruise';
  const atSea = isAtSeaLocation(name);
  const showDock = Boolean(meta.isEmbarkation || meta.isDebarkation);

  const [placeQuery, setPlaceQuery] = React.useState('');
  const [placeResults, setPlaceResults] = React.useState<PlaceSearchResult[]>([]);
  const [placeSearching, setPlaceSearching] = React.useState(false);

  const patchMeta = (patch: Partial<TripCityVisitMeta>): void => {
    onChange(updateCityMeta(trip, cityId, patch));
  };

  const handlePlaceSearch = async (): Promise<void> => {
    const q = placeQuery.trim();
    if (!q) return;
    if (!isGooglePlacesConfigured()) {
      message.warning(tripEditEn.placeSearchUnavailable);
      return;
    }
    setPlaceSearching(true);
    try {
      const results = await searchPlaces({
        textQuery: q,
        cityName: name || q,
        latitude: coords?.latitude,
        longitude: coords?.longitude,
        maxResults: 8,
      });
      setPlaceResults(results);
      if (results.length === 0) {
        message.info(tripEditEn.placeSearchFailed);
      }
    } catch {
      message.error(tripEditEn.placeSearchFailed);
      setPlaceResults([]);
    } finally {
      setPlaceSearching(false);
    }
  };

  const handleSelectPlace = (place: PlaceSearchResult): void => {
    onChange(applyPlaceToCity(trip, cityId, place));
    setPlaceResults([]);
    setPlaceQuery('');
    message.success(tripEditEn.placeCoordsSet);
  };

  const summaryDate = formatCardWeekdayDate(meta.arrivalDate);

  return (
    <Collapse
      className={styles.cityCollapse}
      items={[
        {
          key: cityId,
          label: (
            <div className={styles.cityLabel}>
              <span className={styles.cityLabelName}>{name || tripEditEn.cityNamePlaceholder}</span>
              {summaryDate ? <span className={styles.cityLabelDate}>{summaryDate}</span> : null}
            </div>
          ),
          children: (
            <div className={styles.cityFields}>
              {!atSea ? (
                <Input
                  value={name}
                  onChange={(e) => onChange(renameCity(trip, cityId, e.target.value))}
                  placeholder={
                    isCruise ? tripEditEn.portNamePlaceholder : tripEditEn.cityNamePlaceholder
                  }
                />
              ) : (
                <div className={styles.seaDayName}>{name}</div>
              )}

              {!atSea ? (
                <div className={styles.placeSearch}>
                  <label>{tripEditEn.searchPlace}</label>
                  <div className={styles.placeSearchRow}>
                    <Input
                      value={placeQuery}
                      onChange={(e) => setPlaceQuery(e.target.value)}
                      placeholder={tripEditEn.searchPlacePlaceholder}
                      onPressEnter={() => void handlePlaceSearch()}
                      aria-label={tripEditEn.searchPlacePlaceholder}
                    />
                    <Button
                      onClick={() => void handlePlaceSearch()}
                      loading={placeSearching}
                      disabled={!placeQuery.trim()}
                    >
                      {tripEditEn.searchPlace}
                    </Button>
                  </div>
                  {coords ? (
                    <p className={styles.coordsHint}>
                      {coords.latitude.toFixed(4)}, {coords.longitude.toFixed(4)}{' '}
                      <Button
                        type="link"
                        size="small"
                        onClick={() => onChange(setCityCoords(trip, cityId, null))}
                      >
                        {tripEditEn.clearCoords}
                      </Button>
                    </p>
                  ) : null}
                  {placeSearching ? <Spin size="small" tip={tripEditEn.searchingPlace} /> : null}
                  {placeResults.length > 0 ? (
                    <List
                      size="small"
                      dataSource={placeResults}
                      renderItem={(place) => (
                        <List.Item>
                          <button
                            type="button"
                            className={styles.placeResult}
                            onClick={() => handleSelectPlace(place)}
                          >
                            <strong>{place.name}</strong>
                            <span>{place.address}</span>
                          </button>
                        </List.Item>
                      )}
                    />
                  ) : null}
                </div>
              ) : null}

              {atSea ? (
                <div className={styles.fieldRow}>
                  <label>{tripEditEn.seaDayDate}</label>
                  <DatePicker
                    value={toDay(meta.arrivalDate)}
                    onChange={(d) => {
                      const date = ymd(d);
                      patchMeta({ arrivalDate: date, departureDate: date });
                    }}
                    style={{ width: '100%' }}
                  />
                </div>
              ) : (
                <>
                  <div className={styles.fieldRow}>
                    <label>{tripEditEn.arrivalDate}</label>
                    <DatePicker
                      value={toDay(meta.arrivalDate)}
                      onChange={(d) => patchMeta({ arrivalDate: ymd(d) })}
                      style={{ width: '100%' }}
                    />
                  </div>
                  <div className={styles.fieldRow}>
                    <label>{tripEditEn.arrivalTime}</label>
                    <TimePicker
                      value={toTime(meta.portArrivalHm)}
                      format="h:mm A"
                      use12Hours
                      onChange={(d) => patchMeta({ portArrivalHm: hm(d) })}
                      style={{ width: '100%' }}
                    />
                  </div>
                  <div className={styles.fieldRow}>
                    <label>{tripEditEn.departureDate}</label>
                    <DatePicker
                      value={toDay(meta.departureDate)}
                      onChange={(d) => patchMeta({ departureDate: ymd(d) })}
                      style={{ width: '100%' }}
                    />
                  </div>
                  <div className={styles.fieldRow}>
                    <label>{tripEditEn.departureTime}</label>
                    <TimePicker
                      value={toTime(meta.portDepartureHm)}
                      format="h:mm A"
                      use12Hours
                      onChange={(d) => patchMeta({ portDepartureHm: hm(d) })}
                      style={{ width: '100%' }}
                    />
                  </div>
                </>
              )}

              {isCruise && !atSea ? (
                <>
                  <div className={styles.switchRow}>
                    <span>{tripEditEn.embarkation}</span>
                    <Switch
                      checked={Boolean(meta.isEmbarkation)}
                      onChange={(v) => onChange(setEmbarkDebark(trip, cityId, 'isEmbarkation', v))}
                    />
                  </div>
                  <div className={styles.switchRow}>
                    <span>{tripEditEn.debarkation}</span>
                    <Switch
                      checked={Boolean(meta.isDebarkation)}
                      onChange={(v) => onChange(setEmbarkDebark(trip, cityId, 'isDebarkation', v))}
                    />
                  </div>
                  <div className={styles.fieldRow}>
                    <label>{tripEditEn.portStatus}</label>
                    <Select
                      value={meta.cruisePortStatus ?? undefined}
                      allowClear
                      placeholder={tripEditEn.portStatus}
                      onChange={(v) =>
                        patchMeta({ cruisePortStatus: (v as CruisePortStatus) ?? null })
                      }
                      options={Object.entries(tripEditEn.portStatusOptions).map(([value, label]) => ({
                        value,
                        label,
                      }))}
                      style={{ width: '100%' }}
                    />
                  </div>
                  {showDock ? (
                    <div className={styles.fieldRow}>
                      <label>{tripEditEn.dockNumber}</label>
                      <Input
                        value={meta.dockNumber ?? ''}
                        placeholder={tripEditEn.dockNumberPlaceholder}
                        onChange={(e) => patchMeta({ dockNumber: e.target.value || null })}
                      />
                    </div>
                  ) : null}
                </>
              ) : null}

              <Popconfirm
                title={tripEditEn.removeCityConfirm}
                onConfirm={() => onChange(removeCityFromDraft(trip, cityId))}
              >
                <Button danger type="link" className={styles.removeCity}>
                  {tripEditEn.removeCity}
                </Button>
              </Popconfirm>
            </div>
          ),
        },
      ]}
    />
  );
};

export default EditTripCityPanel;
